import React, { useState, useEffect } from "react";

import {
  Box,
  TextField,
  Button,
  Typography,
  FormControlLabel,
  Switch,
  Grid,
  Alert,
  CircularProgress,
} from "@mui/material";
import Select from "react-select";
import axios from "axios";
import apiUrl from "../../components/api";


const InvoiceForm = (props) => {
  // Initialize state with either existing values or values from the charge if provided
  const [amount, setAmount] = useState(() => {
    // If we have multiple charges, sum their amounts
    if (props.charge?.multipleCharges) {
      return props.charge.multipleCharges.reduce((sum, charge) => sum + (charge.amount || 0), 0);
    }
    return props.charge?.amount || props.amount || 0;
  });
  
  const [remainingAmount, setRemainingAmount] = useState(() => {
    // If we have multiple charges, sum their remaining amounts
    if (props.charge?.multipleCharges) {
      return props.charge.multipleCharges.reduce((sum, charge) => sum + (charge.remaining_amount || 0), 0);
    }
    return props.charge?.remaining_amount || props.remaining_amount || 0;
  });
  
  const [paid, setPaid] = useState(props.paid || false);
  const [charges, setCharges] = useState(() => {
    // If we have multiple charges from selection, use those
    if (props.charge?.multipleCharges) {
      return props.charge.multipleCharges;
    }
    return props.charge ? [props.charge] : props.charges || [];
  });
  const [chargeOptions, setChargeOptions] = useState([]);
  const [attachments, setAttachments] = useState(props.attachments || []);
  const [invoiceNumber, setInvoiceNumber] = useState(
    props.invoice_number || ""
  );
  
  // Initialize property state based on charge property or props
  const [property, setProperty] = useState(() => {
    if (props.charge?.property) {
      // Handle both object and string property references
      const propId = typeof props.charge.property === 'object' 
        ? props.charge.property._id 
        : props.charge.property;
        
      return {
        value: propId,
        label: typeof props.charge.property === 'object' 
          ? props.charge.property.name
          : 'Selected Property' // Will be updated when properties are loaded
      };
    }
    return props.property || null; // Initialize as null instead of empty string
  });

  const [properties, setProperties] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState("");
  const [formErrors, setFormErrors] = useState({
    amount: "",
    remainingAmount: "",
    invoiceNumber: "",
    property: "",
  });

  const [formValid, setFormValid] = useState(false);
  const [disableSubmit, setDisableSubmit] = useState(true);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const user = JSON.parse(localStorage.getItem("user"));
  const token = user ? user.token : null;

  // Fetch properties and update property label if needed
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await axios.get(`${apiUrl}/property/getPropertiesNames`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setProperties(response.data.propertiesNames);
        
        // If we have a property from charge but only the ID, update the label
        if (props.charge?.property && typeof props.charge.property !== 'object' && property) {
          const foundProperty = response.data.propertiesNames.find(p => p._id === props.charge.property);
          if (foundProperty) {
            setProperty({
              value: foundProperty._id,
              label: foundProperty.name
            });
          }
        }
      } catch (error) {
        console.error("Error fetching properties:", error);
      }
    };

    fetchProperties();
  }, []);

  // Fetch charges and set up charge options
  useEffect(() => {
    const fetchCharges = async () => {
      try {
        const response = await axios.get(`${apiUrl}/charge`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        
        setChargeOptions(
          response.data.charges.map((charge) => ({
            value: charge,
            label: `${formatCurrency(charge.amount)} - ${new Date(charge.created_at).toLocaleDateString()}`,
          }))
        );
        
        // If we have a charge from props but it's not in the chargeOptions yet, add it
        if (props.charge && !chargeOptions.some(option => option.value._id === props.charge._id)) {
          setChargeOptions(prev => [
            ...prev,
            {
              value: props.charge,
              label: `${formatCurrency(props.charge.amount)} - ${new Date(props.charge.created_at).toLocaleDateString()}`
            }
          ]);
        }
      } catch (error) {
        console.error("Error fetching charges:", error);
      }
    };

    fetchCharges();
  }, [props.charge]);
  
  // Helper function to format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  useEffect(() => {
    const isValid =
      amount > 0 &&
      remainingAmount >= 0 &&
      invoiceNumber.trim() !== "" &&
      property !== null;

    setFormValid(isValid);
    setDisableSubmit(!isValid);
  }
  , [amount, remainingAmount, invoiceNumber, property]);
  
  useEffect(() => {
    if (formSubmitted) {
      setFormSubmitted(false);
    }
  }, [formSubmitted]);
  
  const handleAmountChange = (event) => {
    const value = event.target.value;
    setAmount(value);
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      amount: value > 0 ? "" : "Amount must be greater than 0",
    }));
  };
  
  const handleRemainingAmountChange = (event) => {
    const value = event.target.value;
    setRemainingAmount(value);
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      remainingAmount:
        value >= 0 ? "" : "Remaining amount must be greater than or equal to 0",
    }));
  };
  
  const handlePaidChange = (event) => {
    setPaid(event.target.checked);
  };
  
  const handleInvoiceNumberChange = (event) => {
    const value = event.target.value;
    setInvoiceNumber(value);
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      invoiceNumber: value.trim() !== "" ? "" : "Invoice number is required",
    }));
  };
  
  const handlePropertyChange = (selectedOption) => {
    setProperty(selectedOption);
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      property: selectedOption ? "" : "Property is required",
    }));
  };
  
  const handleChargeChange = (selectedOptions) => {
    setCharges(selectedOptions.map((option) => option.value));
  };
  
  const handleAttachmentChange = (event) => {
    setAttachments(event.target.files);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Additional validation before submission
    const validationErrors = {};
    if (!invoiceNumber || invoiceNumber.trim() === "") {
      validationErrors.invoiceNumber = "Invoice number is required";
    }
    
    if (!property || !property.value) {
      validationErrors.property = "Property is required";
    }
    
    if (Number(amount) <= 0) {
      validationErrors.amount = "Amount must be greater than 0";
    }
    
    if (Number(remainingAmount) < 0) {
      validationErrors.remainingAmount = "Remaining amount cannot be negative";
    }
    
    // If there are validation errors, update the state and stop submission
    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      return;
    }
    
    setLoading(true);
    setError("");
    setSuccess(false);
    setMessage("");

    try {
      // Use direct object instead of FormData for clearer data transfer
      const invoiceData = {
        amount: Number(amount),
        remaining_amount: Number(remainingAmount),
        paid: paid,
        charges: charges.map(charge => 
          typeof charge === 'object' && charge._id ? charge._id : charge
        ),
        invoice_number: invoiceNumber,
        property: property.value
      };

      const response = await axios.post(`${apiUrl}/invoice`, invoiceData, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      setSuccess(true);
      setMessage(response.data.message);
      setFormSubmitted(true);
      if (props.onSuccess) {
        props.onSuccess(response.data.invoice); // Pass the created invoice back to the parent component
      } else if (props.onInvoiceCreated) {
        props.onInvoiceCreated(response.data.invoice);
      }
      setLoading(false);
      setFormErrors({
        amount: "",
        remainingAmount: "",
        invoiceNumber: "",
        property: "",
      });
    } catch (error) {
      console.error("Error creating invoice:", error);
      setError(error.response?.data?.message || "An error occurred");
      
      // If we get validation errors from the server, display them
      if (error.response?.data?.data && Array.isArray(error.response.data.data)) {
        const serverErrors = {};
        error.response.data.data.forEach(err => {
          serverErrors[err.path] = err.msg;
        });
        setFormErrors(prev => ({ ...prev, ...serverErrors }));
      }
      
      setLoading(false);
    }
  };
  
  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: "flex",
        width: "100%",
        flexDirection: "column",
        gap: 2,
        padding: 2,
        borderRadius: 1,
        boxShadow: 1,
        backgroundColor: "background.paper",
      }}
    >
      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{message}</Alert>}
      <Typography variant="h6">Invoice Form</Typography>
      <TextField
        label="Amount"
        type="number"
        value={amount}
        onChange={handleAmountChange}
        error={!!formErrors.amount}
        helperText={formErrors.amount}
        disabled={props.readOnly}
        required
      />
      <TextField
        label="Remaining Amount"
        type="number"
        value={remainingAmount}
        onChange={handleRemainingAmountChange}
        error={!!formErrors.remainingAmount}
        helperText={formErrors.remainingAmount}
        required
        disabled={props.readOnly}
      />
      <FormControlLabel
        control={<Switch checked={paid} onChange={handlePaidChange} />}
        label="Paid"
        disabled={props.readOnly}
      />
      <TextField
        label="Invoice Number"
        value={invoiceNumber}
        onChange={handleInvoiceNumberChange}
        error={!!formErrors.invoiceNumber}
        helperText={formErrors.invoiceNumber}
        required
        disabled={props.readOnly}
      />
      <Select
        options={properties.map((property) => ({
          value: property._id,
          label: property.name,
        }))}
        onChange={handlePropertyChange}
        placeholder="Select Property"
        isClearable
        isDisabled={props.readOnly || props.charge}
        styles={{
          control: (base) => ({
            ...base,
            borderColor: formErrors.property ? "red" : base.borderColor,
          }),
        }}
        value={property}
        isSearchable
      />
      {formErrors.property && (
        <Typography color="error">{formErrors.property}</Typography>
      )}
      <Select
        options={chargeOptions}
        onChange={handleChargeChange}
        isMulti
        placeholder="Select Charges"
        isDisabled={props.readOnly || props.charge}
        styles={{
          control: (base) => ({
            ...base,
            borderColor: formErrors.charge ? "red" : base.borderColor,
          }),
        }}
        value={charges.map(charge => {
          const chargeId = typeof charge === 'object' && charge._id ? charge._id : charge;
          const option = chargeOptions.find(opt => 
            opt.value._id === chargeId || opt.value === chargeId
          );
          return option || {
            value: charge,
            label: typeof charge === 'object' && charge.amount 
              ? `${formatCurrency(charge.amount)} - ${new Date(charge.created_at).toLocaleDateString()} - ${chargeId}`
              : `Charge ID: ${chargeId}`
          };
        })}
        isSearchable
      />
      {/* <TextField
        type="file"
        onChange={handleAttachmentChange}
        inputProps={{ multiple: true }}
        disabled={props.readOnly}
        helperText="Select multiple files"
        fullWidth
      /> */}
      <Button
        type="submit"
        variant="contained"
        color="primary"
        disabled={disableSubmit || loading || props.readOnly}
        startIcon={loading && <CircularProgress size={20} />}
      >
        {loading ? "Submitting..." : "Submit"}
      </Button>
      {formSubmitted && (
        <Alert severity="success">Form submitted successfully!</Alert>
      )}
      {formErrors.amount && (
        <Typography color="error">{formErrors.amount}</Typography>
      )}
      {formErrors.remainingAmount && (
        <Typography color="error">{formErrors.remainingAmount}</Typography>
      )}
      {formErrors.invoiceNumber && (
        <Typography color="error">{formErrors.invoiceNumber}</Typography>
      )}

      {formErrors.property && (
        <Typography color="error">{formErrors.property}</Typography>
      )}
      {formErrors.charge && (
        <Typography color="error">{formErrors.charge}</Typography>
      )}
      {formErrors.attachments && (
        <Typography color="error">{formErrors.attachments}</Typography>
      )}
    </Box>
  );
}
export default InvoiceForm;


